/**
 * Variable Translation Helpers
 *
 * Shared utilities for translating variable names to user-friendly labels and tooltips.
 * Supports BO translations, context-based translations, and facilitator report translations
 * with comprehensive fallback chains.
 *
 * Uses types from pure-js/types to ensure type safety and alignment with backend types.
 */

import type { TFunction } from 'i18next'
import type { ImprovementAreaVariable } from 'pure-js/types/Report'
import { PSYCHOLOGICAL_SAFETY_QUESTION_COPY_PATHS } from 'pure-js/types/Session'
import { formatVariableName } from '../../features/facilitator/facilitatorReportFlow/helpers/variableNameFormatter'

/**
 * Flexible translation function type that accepts both i18next TFunction and simple string functions
 */
type TranslationFunction = TFunction | ((key: string, options?: { ns?: string; defaultValue?: string }) => string)

/**
 * Union type for all possible variable names
 * Combines player, team, and reflection question variables
 *
 * Uses types from pure-js/types to ensure alignment with backend types.
 * Note: Some variables may not be in ImprovementAreaVariable but are still valid
 * (e.g., team variables used in whatIsWorking, reflection questions).
 *
 * We use string as the base type for flexibility while preferring typed variables
 * when available from pure-js/types.
 */
export type VariableName =
  | ImprovementAreaVariable
  | 'teamReflectionQuestionOne'
  | 'teamReflectionQuestionTwo'
  | 'teamReflectionQuestionThree'
  | 'teamReflectionQuestionFour'
  | 'teamReflectionQuestionFive'
  | 'teamReflectionQuestionSix'
  | 'teamReflectionQuestionSeven'
  | string // Allow string for flexibility, but prefer typed variables from pure-js/types

/**
 * Options for variable label translation
 */
export interface VariableLabelOptions {
  /** Context for player variables ('strengths' | 'weaknesses') */
  context?: 'strengths' | 'weaknesses'
  /** Type of translation ('label' | 'tooltip'), defaults to 'label' */
  type?: 'label' | 'tooltip'
  /** Section key for facilitator reports ('whatIsWorking' | 'improvementAreas') */
  sectionKey?: 'whatIsWorking' | 'improvementAreas'
}

/**
 * Check if a translation result is valid (not missing or empty)
 *
 * @param translation - The translation result from i18next
 * @param key - The original translation key
 * @returns True if translation is valid (exists and not empty)
 */
function isValidTranslation(translation: string, key: string): boolean {
  return Boolean(translation && translation !== key && translation.trim() !== '')
}

/**
 * Try to get BO translation for a variable
 *
 * @param variable - The variable name
 * @param type - Type of translation ('label' | 'tooltip')
 * @param t - Translation function
 * @returns Translation string if found, empty string otherwise
 */
function tryBOTranslation(variable: string, type: 'label' | 'tooltip', t: TranslationFunction): string {
  const boKey = `tab.${variable}.${type}`
  const boTranslation = typeof t === 'function' ? t(boKey, { ns: 'translation', defaultValue: '' }) : ''
  return isValidTranslation(boTranslation, boKey) ? boTranslation : ''
}

/**
 * Try to get context-based translation for a variable
 *
 * @param variable - The variable name
 * @param context - Context ('strengths' | 'weaknesses')
 * @param type - Type of translation ('label' | 'tooltip')
 * @param t - Translation function
 * @returns Translation string if found, empty string otherwise
 */
function tryContextTranslation(
  variable: string,
  context: 'strengths' | 'weaknesses',
  type: 'label' | 'tooltip',
  t: TranslationFunction
): string {
  const contextKey = `player.${context}.${variable}.${type}`
  const contextTranslation = typeof t === 'function' ? t(contextKey, { ns: 'feedbackTexts', defaultValue: '' }) : ''

  if (isValidTranslation(contextTranslation, contextKey)) {
    return contextTranslation
  }

  // Cross-context fallback: if weaknesses not found, try strengths
  if (context === 'weaknesses') {
    const strengthKey = `player.strengths.${variable}.${type}`
    const strengthTranslation = typeof t === 'function' ? t(strengthKey, { ns: 'feedbackTexts', defaultValue: '' }) : ''
    if (isValidTranslation(strengthTranslation, strengthKey)) {
      return strengthTranslation
    }
  }

  return ''
}

/**
 * Try to get facilitator report translation for a variable
 *
 * @param variable - The variable name
 * @param sectionKey - Section key ('whatIsWorking' | 'improvementAreas')
 * @param t - Translation function
 * @returns Translation string if found, empty string otherwise
 */
function tryFacilitatorTranslation(
  variable: string,
  sectionKey: 'whatIsWorking' | 'improvementAreas',
  t: TranslationFunction
): string {
  const facilitatorKey = `facilitator.reportFlow.sections.${sectionKey}.${variable}.label`
  const facilitatorTranslation =
    typeof t === 'function' ? t(facilitatorKey, { ns: 'translation', defaultValue: '' }) : ''
  return isValidTranslation(facilitatorTranslation, facilitatorKey) ? facilitatorTranslation : ''
}

/**
 * Get label or tooltip for a variable with comprehensive fallback support
 *
 * **Translation Priority Order:**
 *
 * **For Labels:**
 * 1. BO translation: `tab.${variable}.label` (from `translation` namespace)
 * 2. Context-based: `player.${context}.${variable}.label` (from `feedbackTexts` namespace)
 * 3. Cross-context fallback: If weaknesses, try `player.strengths.${variable}.label`
 * 4. Facilitator report: `facilitator.reportFlow.sections.${sectionKey}.${variable}.label`
 * 5. Format variable name: `formatVariableName(variable)`
 *
 * **For Tooltips:**
 * 1. BO translation: `tab.${variable}.tooltip` (from `translation` namespace)
 * 2. Context-based: `player.${context}.${variable}.tooltip` (from `feedbackTexts` namespace)
 * 3. Cross-context fallback: If weaknesses, try `player.strengths.${variable}.tooltip`
 * 4. Full label text: Returns the full label (ensures tooltip shows complete text when label is truncated)
 *
 * @param variable - The variable name (e.g., "playerSharedResource")
 *                   Should use types from pure-js/types when possible for type safety
 * @param t - Translation function from i18next
 * @param options - Optional configuration
 * @param options.context - Context for player variables ('strengths' | 'weaknesses')
 * @param options.type - Type of translation ('label' | 'tooltip'), defaults to 'label'
 * @param options.sectionKey - Section key for facilitator reports ('whatIsWorking' | 'improvementAreas')
 * @returns Translated string (never undefined - tooltips fallback to full label text)
 *
 * @example
 * ```typescript
 * // Get label for a strength variable
 * const label = getVariableLabel('playerSharedResource', t, { context: 'strengths' })
 *
 * // Get tooltip for a weakness variable
 * const tooltip = getVariableLabel('playerSharedResource', t, { context: 'weaknesses', type: 'tooltip' })
 *
 * // Get label for facilitator report
 * const reportLabel = getVariableLabel('teamSharedResource', t, { sectionKey: 'whatIsWorking' })
 * ```
 */
export function getVariableLabel(
  variable: VariableName,
  t: TranslationFunction,
  options?: VariableLabelOptions
): string {
  const type = options?.type || 'label'
  const context = options?.context
  const sectionKey = options?.sectionKey

  // For tooltips, follow priority chain
  if (type === 'tooltip') {
    // 1. Try BO tooltip translation
    const boTooltip = tryBOTranslation(variable, 'tooltip', t)
    if (boTooltip) return boTooltip

    // 2. Try context-based tooltip translation
    if (context) {
      const contextTooltip = tryContextTranslation(variable, context, 'tooltip', t)
      if (contextTooltip) return contextTooltip
    }

    // 3. Fallback: return full label text (ensures tooltip shows complete text when label is truncated)
    return getVariableLabel(variable, t, { ...options, type: 'label' })
  }

  // For labels, follow the full priority chain
  // 1. Try BO translation
  const boLabel = tryBOTranslation(variable, 'label', t)
  if (boLabel) return boLabel

  // 2. Try context-based translation
  if (context) {
    const contextLabel = tryContextTranslation(variable, context, 'label', t)
    if (contextLabel) return contextLabel
  }

  // 3. Try facilitator report translation
  if (sectionKey) {
    const facilitatorLabel = tryFacilitatorTranslation(variable, sectionKey, t)
    if (facilitatorLabel) return facilitatorLabel
  }

  // 4. Final fallback: format variable name
  return formatVariableName(variable)
}

/** The short name of a psychological-safety survey question, a team's (`team…`) or a person's (`player…`). */
export function getPsychologicalSafetyQuestionLabel(questionKey: string, t: TranslationFunction): string {
  const labels = PSYCHOLOGICAL_SAFETY_QUESTION_COPY_PATHS.map((path) =>
    t(`${path}.${questionKey}`, { defaultValue: questionKey })
  )

  return labels.find((label) => label !== questionKey) ?? questionKey
}
